import { useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { ActiveCallBar } from '../call-bar/call-bar';
import { DesktopActiveCallBarTrail } from '../call-bar/desktop-call-bar-trail';
import { IncomingCallBar } from '../incoming-call-bar/incoming-cb';
import { IncomingCallBarPreview } from '../incoming-call-bar/incoming-cb-preview';
import { CallBarProps } from './types';

const getDesktopCallBarState = (numOfIncomingCalls: number, hasActiveCalls: boolean) => {
  if (!hasActiveCalls) {
    return {
      showPreview: false,
      isActiveCallExpanded: false,
      showIncomingCallBar: true,
    };
  } else if (hasActiveCalls && numOfIncomingCalls === 0) {
    return {
      showPreview: false,
      isActiveCallExpanded: true,
      showIncomingCallBar: false,
    };
  } else if (hasActiveCalls && numOfIncomingCalls > 0) {
    return {
      showPreview: true,
      isActiveCallExpanded: true,
      showIncomingCallBar: false,
    };
  }

  return {
    showPreview: false,
    isActiveCallExpanded: false,
    showIncomingCallBar: false,
  };
};

export const DesktopCallBars = ({
  numOfIncomingCalls,
  isOpen,
  toggleActiveCallBar,
  showIncomingCallsUI,
  hasActiveCalls,
  callData,
}: CallBarProps) => {
  const [toggleOpenIncomingCallBar, setToggleOpenIncomingCallBar] = useState<boolean>(false);
  const showTrailingIcon = hasActiveCalls && toggleOpenIncomingCallBar;
  const showActiveCallBar = hasActiveCalls;

  const { showPreview, isActiveCallExpanded, showIncomingCallBar } = useMemo(() => {
    if (toggleOpenIncomingCallBar) {
      return {
        showPreview: false,
        isActiveCallExpanded: false,
        showIncomingCallBar: true,
      };
    }
    return getDesktopCallBarState(numOfIncomingCalls, hasActiveCalls);
  }, [numOfIncomingCalls, hasActiveCalls, toggleOpenIncomingCallBar]);

  useEffect(() => {
    if (numOfIncomingCalls === 0) {
      setToggleOpenIncomingCallBar(false);
    }
  }, [numOfIncomingCalls]);

  const onClickIncomingCallBarPreview = useCallback(() => {
    setToggleOpenIncomingCallBar(true);
  }, [setToggleOpenIncomingCallBar]);

  const resetExpandedState = useCallback(() => {
    setToggleOpenIncomingCallBar(false);
  }, [setToggleOpenIncomingCallBar]);

  return (
    <>
      {showActiveCallBar && (
        <ActiveCallBar
          isExpanded={isActiveCallExpanded}
          toggleOpen={toggleActiveCallBar}
          css={css`
            min-width: ${isActiveCallExpanded ? '344px' : 'auto'};
          `}
        >
          <DesktopActiveCallBarTrail isOpen={isOpen} />
        </ActiveCallBar>
      )}
      {showIncomingCallBar && showIncomingCallsUI && (
        <IncomingCallBar
          showTrailingIcon={showTrailingIcon}
          numOfIncomingCalls={numOfIncomingCalls}
          callData={callData}
          onHandleTrailIcon={resetExpandedState}
          onEndCall={resetExpandedState}
          onAnswerCall={resetExpandedState}
        />
      )}
      {showPreview && showIncomingCallsUI && (
        <IncomingCallBarPreview
          animationX={-50}
          numOfIncomingCalls={numOfIncomingCalls}
          onClick={onClickIncomingCallBarPreview}
        />
      )}
    </>
  );
};
